import axiosInstance from "../../services/axiosInstance";
import { axiosWithAbort } from "../../services/axiosWithAbort";
import handleAPIError from "../../services/toast";
import {
  normalizeBlobAPIError,
  throwIfBlobContainsAPIData,
} from "../../services/blob-response";
import { getPercentageRequestValue } from "../utils/percentageValue";

//////// 轉換下拉式選單資料格式
const formatter = (list = []) =>
  list
    .map((item) => {
      // 取出依插入順序排列的鍵
      const keys = Object.keys(item);
      if (keys.length < 2) return null; // 目的：防呆，至少要兩個鍵

      const [valueKey, labelKey, specialKey] = keys; // 目的：抓前兩個鍵
      return {
        label: item[labelKey], // 目的：前鍵的值當 label
        value: item[valueKey], // 目的：後鍵的值當 value
        ...(specialKey
          ? { [specialKey]: item[specialKey] ?? "" } // ✅ 動態鍵名要用 [specialKey]
          : {}),
      };
    })
    .filter(Boolean); // 目的：移除不合法項目（鍵不足）

const toSafeNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export const emissionActivityModalAPI = {
  ////////  1.1 下拉式選單
  async stationaryCombustionEmissionActivityAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat1_1/dropdown_list`);
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 排放活動列表 (services版本)
  async getEAList(requestBody, routeCat) {
    const response = await axiosWithAbort("EAList", {
      method: "POST",
      url: `/api/${routeCat}/activity_list`,
      data: requestBody,
    });

    return response.data.data ?? [];
  },

  //////// 排放活動 biomass (services版本)
  async getBiomassEAList(requestBody, routeCat) {
    const response = await axiosInstance.post(
      `/api/${routeCat}/activity_list/biomass`,
      requestBody,
    );
    return response.data.data;
  },

  async getRemovalMitigationList(requestBody) {
    const response = await axiosWithAbort("RMList", {
      method: "POST",
      url: `/api/cat6_2/removal_mitigation_list`,
      data: requestBody,
    });

    if (response === null) return null;
    return response.data.data ?? [];
  },

  async getRemovalEmissionList(requestBody) {
    const response = await axiosInstance.post(
      `/api/cat6_2/removal_emission_list`,
      requestBody,
    );

    return response.data.data ?? [];
  },

  async createRemovalMitigation(requestBody) {
    const response = await axiosInstance.post(
      `/api/cat6_2/create_removal_mitigation`,
      requestBody,
    );

    return response.data;
  },

  async updateRemovalMitigation(id, requestBody) {
    const response = await axiosInstance.put(
      `/api/cat6_2/removal_mitigation/${id}`,
      requestBody,
    );

    return response.data;
  },

  async deleteRemovalMitigation(id) {
    const response = await axiosInstance.delete(
      `/api/cat6_2/removal_mitigation/${id}`,
    );

    const { success } = response.data;
    if (success === false) {
      handleAPIError(null, {
        apiData: response.data
      });
    }

    return response.data.data ?? response.data;
  },

  //////// 1.1 Add emission activity
  async cat1_1AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData.companyId,
      year: year,
      category: "1_1",
      activityId: Number(EAData?.activityId) || 0,
      standardId: standardId || 1,
      name: EAData?.itemName || "",
      isBiomassActivity: EAData?.isBiomassActivity || false,
      isBiomassSeparate: EAData?.isBiomassSeparate || false,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
    };

    //////// 判斷 name 和 activityId 是否有填入
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Name field cannot be empty！",
        "The Name field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat1_1/`, requestBody);
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      console.error("Error adding cat1_1 list data:", error);
      throw error;
    }
  },

  //////// 1.2下拉式選單
  async mobileCombustionEmissionActivityAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat1_2/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 1.2排放活動列表
  async cat1_2EmissionActivityListAPI(cId, year, keyword) {
    const requestBody = {
      companyIds: cId,
      year: year,
      category: "1_2",
      keyword: keyword,
    };
    try {
      const response = await axiosInstance.post(
        `/api/cat1_2/activity_list`,
        requestBody,
      );
      return response.data.data; // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching cat1_2 list data:");
      console.error("Error fetching cat1_2 list data:", error);
      return []; // 失敗時回傳 null
    }
  },

  //////// 1.2新增排放活動
  async cat1_2AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "1_2",
      activityId: Number(EAData.activityId) || 0,
      name: EAData.itemName || "",
      isBiomassActivity: EAData?.isBiomassActivity || false,
      isBiomassSeparate: EAData?.isBiomassSeparate || false,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷 name 和 activityId 是否有填入
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be emtpy！",
        "The Company field cannot be emtpy！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Name field cannot be empty！",
        "The Name field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat1_2/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat1_2 list data:");
      console.error("Error fetching cat1_2 list data:", error);
      throw error;
    }
  },

  //////// 1.3
  async directGHGEmissionsEmissionActivityAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat1_3/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 1.3石化燃料
  async cat1_3Fossil() {
    return [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ];
  },

  //////// 1.3新增排放活動
  async cat1_3AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "1_3",
      activityId: Number(EAData.activityId) || 0,
      name: EAData?.itemName || "",
      isFossil: EAData?.isFossil ?? null,
      otherEFNote: EFData?.otherEFNote || "",
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷 name 和 activityId 是否有填入
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Name field cannot be empty！",
        "The Name field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    if (requestBody.isFossil === null || requestBody.isFossil === undefined) {
      handleAPIError(
        "The fossil-origin methan release field cannot be empty！",
        "The fossil-origin methan release field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat1_3/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat1_3 list data:");
      console.error("Error fetching cat1_3 list data:", error);
      throw error;
    }
  },

  //////// 1.4
  ////設備類別
  async fugitiveEmisionsEquipmentCategoryAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat1_4/equipment_category_dropdown_list`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  ////排放活動下拉式選單
  async fugitiveEmissionActivityAPI(equipmentCategoryId) {
    if (!equipmentCategoryId) {
      return [
        {
          label: "Please fill in the Equipment Category field first.",
          value: "none",
        },
      ];
    }

    try {
      const response = await axiosInstance.get(
        `/api/cat1_4/activity_dropdown_list/${equipmentCategoryId}`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  ////計算方法下拉式選單
  async fugitiveCalculationMethodAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat1_4/refrigerant_calculation_dropdown_list`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  ////冷媒型號下拉式選單
  async fugitiveRefrigerantAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat1_4/refrigerant_dropdown_list`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 1.4石化燃料
  async cat1_4Fossil() {
    return [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ];
  },

  ////////1.4 新增排放活動
  async cat1_4AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "1_4",
      activityId: Number(EAData.activityId) || null, // 確保是數字，預設為 0
      name: EAData?.itemName || null,
      equipmentCategoryId: Number(EAData.equipmentCategoryId) || null, // 確保是數字，預設為 0
      refrigerantId: EAData.refrigerantId || "",
      refrigerantCalculationId: Number(EAData.refrigerantCalculationId) || 0, // 確保是數字，預設為 EAData
      workingDays: Number(EAData.workingDays) || 0, // 確保是數字，預設為 0
      value: Number(EAData.value), // 確保是數字，預設為 1
      isFossil: EAData?.isFossil ?? null,
      customFugitiveEmissionRate: getPercentageRequestValue(
        EAData?.customFugitiveEmissionRate,
      ),
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }

    if (!requestBody.equipmentCategoryId) {
      handleAPIError(
        "The Device Type field cannot be empty！",
        "The Device Type field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Name / Number field cannot be empty！",
        "The Name / Number field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    if (requestBody.isFossil === null || requestBody.isFossil === undefined) {
      handleAPIError(
        "The fossil-origin methan release field cannot be empty！",
        "The fossil-origin methan release field cannot be empty！",
      );
    }

    if (requestBody.refrigerantCalculationId === 3) {
      if (requestBody.value <= 0 || requestBody.value > 100) {
        handleAPIError(
          "The Input value must be greater than 0 and less than or equal to 100.！",
          "The Input value must be greater than 0 and less than or equal to 100.！",
        );
      }
    }
    ////當選擇冷媒
    // if (requestBody.equipmentCategoryId === 4) {
    //   if (!requestBody.refrigerantId) {
    //     handleAPIError(
    //       "The Refrigerant Model No. field cannot be empty！",
    //       "The Refrigerant Model No. field cannot be empty！"
    //     );
    //   }
    // }
    ////當化糞池選員工人數
    // if (requestBody.activityId === 14024) {
    //   if (!requestBody.workingDays) {
    //     handleAPIError(
    //       "The Working Days (Year) field cannot be empty！",
    //       "The Working Days (Year) field cannot be empty！"
    //     );
    //   }
    // }

    try {
      const response = await axiosInstance.post(`/api/cat1_4/`, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error adding cat1_4 list data:");
      console.error("Error adding cat1_4 list data:", error);
      throw error;
    }
  },

  //////// 2.1
  async importedElectricityEnergyTypeAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat2_1/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 1.5
  async LULUCFEAAPI() {
    try {
      const response = await axiosInstance.get("/api/cat1_5/dropdown_list");
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching 1_5 dropdown list:");
      console.error("Error fetching data:", error);
    }
  },

  async cat1_5EmissionActivityListAPI(cId, year, keyword) {
    const requestBody = {
      companyIds: cId,
      year: year,
      category: "1_5",
      keyword: keyword,
    };
    try {
      const response = await axiosInstance.post(
        `/api/cat1_5/activity_list`,
        requestBody,
      );
      return response.data.data; // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching cat1_5 list data:");
      console.error("Error fetching cat1_5 list data:", error);
      return [];
    }
  },

  //////// 1.5 新增排放活動
  async cat1_5AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData.companyId,
      year: year,
      category: "1_5",
      name: EAData?.itemName || "",
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷 name 和 activityId 是否有填入
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Name field cannot be empty！",
        "The Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat1_5/`, requestBody);

      const { success, error: errMsg } = response.data;

      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error adding cat1_5 data:");
      console.error("Error adding cat1_5 data:", error);
      throw error;
    }
  },

  //////// 2.1新增排放活動
  async cat2_1AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "2_1",
      activityId: Number(EAData?.activityId) || 0,
      name: EAData?.itemName || "",
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷 name 和 activityId 是否有填入
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Energy Supplier or Region field cannot be empty！",
        "The Energy Supplier or Region field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Energy Type field cannot be empty！",
        "The Energy Type field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat2_1/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat2_1 list data:");
      console.error("Error fetching cat2_1 list data:", error);
      throw error;
    }
  },

  //////// 2.2
  async importedEnergyEnergyTypeAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat2_2/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 2.2 ghg activitydropdown
  async ghg2_2ActivityDropdown() {
    try {
      const response = await axiosInstance.get(
        `/api/cat2_2/dropdown_list/ghg/cat2_2`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 2.2新增排放活動
  async cat2_2AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "2_2",
      activityId: Number(EAData.activityId) || null,
      name: EAData.itemName || null,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷 name 和 activityId 是否有填入
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Energy Supplier or Region field cannot be empty！",
        "The Energy Supplier or Region field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Energy Type field cannot be empty！",
        "The Energy Type field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat2_2/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat2_2 list data");
      console.error("Error fetching cat2_2 list data:", error);
      throw error;
    }
  },

  //////// 2.3 ghg activitydropdown
  async ghg2_3ActivityDropdown() {
    try {
      const response = await axiosInstance.get(
        `/api/cat2_2/dropdown_list/ghg/cat2_3`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 2.4 ghg activitydropdown
  async ghg2_4ActivityDropdown() {
    try {
      const response = await axiosInstance.get(
        `/api/cat2_2/dropdown_list/ghg/cat2_4`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3.1 3.2 IsDistanceNotChange
  //////// 是否有運輸API
  async isDistanceChangeAPI() {
    return [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ];
  },

  //////// 3.1
  ////物流方式
  async upstreamTransportAndDistributionForGoodsLogisticsMethodAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat3_1/mode_dropdown_list`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3計算方法
  async cat3Calculation(cat) {
    try {
      const response = await axiosInstance.get(
        `/api/cat3/calculation_dropdown_list/${cat}`,
      );

      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching Calculation dropdown list");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3.1新增排放活動
  async cat3_1AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "3_1",
      name: EAData?.itemName || "",
      vehicleModel: EAData?.vehicleModel || "",
      calculationId: EAData?.calculationId,
      isStatic: EAData?.isStatic,
      origin: EAData.origin || "",
      destination: EAData.destination || "",
      distance: Number(EAData.distance) || 0,
      isBiomassActivity: EAData?.isBiomassActivity || false,
      isBiomassSeparate: EAData?.isBiomassSeparate || false,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId) || 0,
      emissionFactorId: Number(EFData?.emissionFactorId) || 0,
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Goods or Raw material name field cannot be empty！",
        "The Goods or Raw material name field cannot be empty！",
      );
    }
    if (!requestBody.vehicleModel) {
      handleAPIError(
        "The Vehicle Model field cannot be empty！",
        "The Vehicle Model field cannot be empty！",
      );
    }

    if (!requestBody.calculationId) {
      handleAPIError(
        "The Calculation Method field cannot be empty！",
        "The Calculation Method field cannot be empty！",
      );
    }

    if (requestBody.calculationId === 1) {
      if (requestBody.isStatic === null || requestBody.isStatic === undefined) {
        handleAPIError(
          "The Is distance not change ? field cannot be empty！",
          "The Is distance not change ? field cannot be empty！",
        );
      }
    }

    //////// 如果填起點也要填終點
    if (requestBody.origin) {
      if (!requestBody.destination) {
        handleAPIError(
          "The Destination field cannot be empty！",
          "The Destination field cannot be empty！",
        );
      }
    }

    //////// 如果填終點也要填起點
    if (requestBody.destination) {
      if (!requestBody.origin) {
        handleAPIError(
          "The Origin field cannot be empty！",
          "The Origin field cannot be empty！",
        );
      }
    }

    try {
      const response = await axiosInstance.post(`/api/cat3_1/`, requestBody);
      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat3_1 list data");
      console.error("Error fetching cat3_1 list data:", error);
      throw error;
    }
  },

  ////運輸方式
  async upstreamTransportAndDistributionForGoodsTransportMethodAPI(modeId) {
    try {
      const response = await axiosInstance.get(
        `/api/cat3_1/activity_dropdown_list/${modeId}`,
      );

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  ////計算方式
  async cat3CalculationMethodAPI() {
    return [{ label: "Weight Distance", value: 0 }];
  },

  //////// 3.2
  ////物流方式
  async downstreamTransportAndDistributionForGoodsLogisticsMethodAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat3_2/mode_dropdown_list`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  ////運輸方式
  async downstreamTransportAndDistributionForGoodsTransportMethodAPI(modeId) {
    try {
      const response = await axiosInstance.get(
        `/api/cat3_2/activity_dropdown_list/${modeId}`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3.2新增排放活動
  async cat3_2AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "3_2",
      name: EAData?.itemName || "",
      vehicleModel: EAData?.vehicleModel || "",
      calculationId: EAData?.calculationId,
      isStatic: EAData?.isStatic,
      origin: EAData.origin || "",
      destination: EAData.destination || "",
      distance: Number(EAData.distance) || 0,
      isBiomassActivity: EAData?.isBiomassActivity || false,
      isBiomassSeparate: EAData?.isBiomassSeparate || false,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId) || 0,
      emissionFactorId: Number(EFData?.emissionFactorId) || 0,
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Goods or Raw material name field cannot be empty！",
        "The Goods or Raw material name field cannot be empty！",
      );
    }
    if (!requestBody.vehicleModel) {
      handleAPIError(
        "The Vehicle Model field cannot be empty！",
        "The Vehicle Model field cannot be empty！",
      );
    }

    if (!requestBody.calculationId) {
      handleAPIError(
        "The Calculation Method field cannot be empty！",
        "The Calculation Method field cannot be empty！",
      );
    }

    if (requestBody.calculationId === 1) {
      if (requestBody.isStatic === null || requestBody.isStatic === undefined) {
        handleAPIError(
          "The Is distance not change ? field cannot be empty！",
          "The Is distance not change ? field cannot be empty！",
        );
      }
    }

    //////// 如果填起點也要填終點
    if (requestBody.origin) {
      if (!requestBody.destination) {
        handleAPIError(
          "The Destination field cannot be empty！",
          "The Destination field cannot be empty！",
        );
      }
    }

    //////// 如果填終點也要填起點
    if (requestBody.destination) {
      if (!requestBody.origin) {
        handleAPIError(
          "The Origin field cannot be empty！",
          "The Origin field cannot be empty！",
        );
      }
    }

    try {
      const response = await axiosInstance.post(`/api/cat3_2/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat3_2 list data");
      console.error("Error fetching cat3_2 list data:", error);
      throw error;
    }
  },

  //////// 3.3
  async employeeCommutingModeOfTransportationAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat3_3/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3.3新增排放活動
  async cat3_3AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "3_3",
      name: EAData.itemName || "",
      activityId: Number(EAData.activityId) || 0,
      isMassTransit: Number(EAData.isMassTransit) || 0,
      calculationId: Number(EAData.calculationId) || 0,
      origin: EAData.origin || "",
      destination: EAData.destination || "",
      distance: Number(EAData.distance) || 0,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId) || 0,
      emissionFactorId: Number(EFData?.emissionFactorId) || 0,
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Staff Name / No field cannot be empty！",
        "The Staff Name / No field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Transportations field cannot be empty！",
        "The Transportations field cannot be empty！",
      );
    }

    ////////判斷起點終點
    if (requestBody.origin) {
      if (!requestBody.destination) {
        handleAPIError(
          "The Destination field cannot be empty！",
          "The Destination field cannot be empty！",
        );
      }
    }
    if (requestBody.destination) {
      if (!requestBody.origin) {
        handleAPIError(
          "The Origin field cannot be empty！",
          "The Origin field cannot be empty！",
        );
      }
    }

    try {
      const response = await axiosInstance.post(`/api/cat3_3/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat3_3 list data");
      console.error("Error fetching cat3_3 list data:", error);
      throw error;
    }
  },

  //////// 3.4
  async clientAndVisitorTransportModeOfTransportationAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat3_4/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3.4新增排放活動
  async cat3_4AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "3_4",
      name: EAData.itemName || "",
      activityId: Number(EAData.activityId) || 0,
      isMassTransit: Number(EAData.isMassTransit) || 0,
      calculationId: Number(EAData.calculationId) || 0,
      origin: EAData.origin || "",
      destination: EAData.destination || "",
      distance: Number(EAData.distance) || 0,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId) || 0,
      emissionFactorId: Number(EFData?.emissionFactorId) || 0,
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Guest Name field cannot be empty！",
        "The Guest Name field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Transportations field cannot be empty！",
        "The Transportations field cannot be empty！",
      );
    }

    ////////判斷起點終點
    if (requestBody.origin) {
      if (!requestBody.destination) {
        handleAPIError(
          "The Destination field cannot be empty！",
          "The Destination field cannot be empty！",
        );
      }
    }
    if (requestBody.destination) {
      if (!requestBody.origin) {
        handleAPIError(
          "The Origin field cannot be empty！",
          "The Origin field cannot be empty！",
        );
      }
    }

    try {
      const response = await axiosInstance.post(`/api/cat3_4/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat3_4 list data");
      console.error("Error fetching cat3_4 list data:", error);
      throw error;
    }
  },

  //////// 3.5
  ////前端提供
  async businessTravelVerificationTypeAPI() {
    return [{ value: 1, label: "Transportation" }];
  },
  ////交通方式
  async businessTravelTransportationMethodAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat3_5/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching data");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 3.5新增排放活動
  async cat3_5AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "3_5",
      modeId: Number(EAData.modeId) || 0,
      address: EAData.address || "",
      name: EAData.itemName || "",
      activityId: Number(EAData.activityId) || 0,
      calculationId: Number(EAData.calculationId) || 0,
      isMassTransit: Number(EAData.isMassTransit) || 0,
      origin: EAData.origin || "",
      destination: EAData.destination || "",
      distance: Number(EAData.distance) || 0,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId) || 0,
      emissionFactorId: Number(EFData?.emissionFactorId) || 0,
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Staff Name / No field cannot be empty！",
        "The Staff Name / No field cannot be empty！",
      );
    }

    if (requestBody.modeId === 1) {
      if (!requestBody.activityId) {
        handleAPIError(
          "The Transportation Method field cannot be empty！",
          "The Transportation Method field cannot be empty！",
        );
      }
    }

    ////////判斷起點終點
    if (requestBody.origin) {
      if (!requestBody.destination) {
        handleAPIError(
          "The Destination field cannot be empty！",
          "The Destination field cannot be empty！",
        );
      }
    }
    if (requestBody.destination) {
      if (!requestBody.origin) {
        handleAPIError(
          "The Origin field cannot be empty！",
          "The Origin field cannot be empty！",
        );
      }
    }

    try {
      const response = await axiosInstance.post(`/api/cat3_5/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat3_5 list data");
      console.error("Error fetching cat3_5 list data:", error);
      throw error;
    }
  },

  //////// cat4 計算方法
  async cat4Calculation() {
    try {
      const response = await axiosInstance.get(
        `/api/cat4/calculation_dropdown_list/4_3`,
      );

      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching Calculation dropdown list");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 4.1
  async goodsPurchasedByAnOrganizationRawMaterialTypeAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat4_1/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching weapons data");
      console.error("Error fetching weapons data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// GHG 3.3
  async ghg3_3ActivityDropdown() {
    try {
      const response = await axiosInstance.get(`/api/cat4_1/dropdown_list/ghg`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching weapons data");
      console.error("Error fetching weapons data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 4.1新增排放活動
  async cat4_1AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "4_1",
      name: EAData.itemName || "",
      goodsName: EAData.goodsName,
      activityId: Number(EAData.activityId) || 0,
      ghgCategory: cat || null,
      categoryISO64: EAData?.categoryISO64 || null,
      ghgActivityNameId: EAData?.ghgActivityNameId || 0,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat4_1/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat4_1 list data");
      console.error("Error fetching cat4_1 list data:", error);
      throw error;
    }
  },

  //////// 4.2
  async capitalGoodsThatArePurchasedAndAmortizedByAnOrganizationProductTypeAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat4_2/dropdown_list`);
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching weapons data");
      console.error("Error fetching weapons data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 4.2新增排放活動
  async cat4_2AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "4_2",
      name: EAData.itemName || "",
      goodsName: EAData.goodsName,
      activityId: Number(EAData.activityId) || 0,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat4_2/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat4_2 list data");
      console.error("Error fetching cat4_2 list data:", error);
      throw error;
    }
  },

  //////// 4.3
  async disposalServicesWasteDisposalMethodAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat4_3/activity_dropdown_list`,
      );
      return formatter(response.data.data); // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching weapons data");
      console.error("Error fetching weapons data:", error);
      return null; // 失敗時回傳 null
    }
  },

  async disposalServicesTransportationMethodAPI() {
    try {
      const response = await axiosInstance.get(
        `/api/cat4_3/transport_dropdown_list`,
      );
      let data = formatter(response.data.data);

      return data; // 返回 API 的資料
    } catch (error) {
      // handleAPIError(error, "Error fetching weapons data");
      console.error("Error fetching weapons data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 是否有運輸API
  async isTransport() {
    return [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ];
  },

  async cat4_3CalculationAPI() {
    return [{ label: "Weight Distance", value: 1 }];
  },

  //////// 4.3新增排放活動
  async cat4_3AddEmissionActivityAPI(
    year,
    EAFormData,
    landEAFormData,
    EFFormData,
    EFLandFormData,
    standardId,
  ) {
    const requestBody = {
      companyId: EAFormData?.companyId,
      year: year,
      category: "4_3",
      name: EAFormData.itemName || "",
      activityId: toSafeNumber(EAFormData.activityId) || 0,
      calculationId: toSafeNumber(EAFormData.calculationId) || 0,
      isTransport: landEAFormData.isTransport || false,
      transportActivityId:
        toSafeNumber(landEAFormData.transportActivityId) || 0,
      origin: landEAFormData.origin || "",
      destination: landEAFormData.destination || "",
      distance: toSafeNumber(landEAFormData.distance) || 0,
      emissionFactorTypeId: toSafeNumber(EFFormData?.emissionFactorTypeId) || 0,
      emissionFactorId: toSafeNumber(EFFormData?.emissionFactorId) || 0,
      landEFTypeId: toSafeNumber(EFLandFormData?.landEFTypeId) || 0,
      landEFId: toSafeNumber(EFLandFormData?.landEFId) || 0,
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Service Provider / Waste Name field cannot be empty！",
        "The Service Provider / Waste Name field cannot be empty！",
      );
    }
    if (!requestBody.activityId) {
      handleAPIError(
        "The Waste Disposal Method field cannot be empty！",
        "The Waste Disposal Method field cannot be empty！",
      );
    }

    if (
      requestBody.isTransport === null ||
      requestBody.isTransport === undefined
    ) {
      handleAPIError(
        "The is Transport Method field cannot be empty！",
        "The is Transport Method field cannot be empty！",
      );
    }

    if (requestBody.isTransport === true) {
      if (
        !requestBody.transportActivityId ||
        requestBody.transportActivityId === 0
      ) {
        handleAPIError(
          "The Transportation Mode field cannot be empty！",
          "The Transportation Mode field cannot be empty！",
        );
      }
    }

    if (requestBody.isTransport === true) {
      if (!requestBody.distance) {
        handleAPIError(
          "The Distance field cannot be empty！",
          "The Distance field cannot be empty！",
        );
      }
    }

    try {
      const response = await axiosInstance.post(`/api/cat4_3/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat4_3 list data");
      console.error("Error fetching cat4_3 list data:", error);
      throw error;
    }
  },

  //////// 4.4新增排放活動
  async cat4_4AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "4_4",
      name: EAData?.itemName || "",
      assetName: EAData?.assetName || "",
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// system database 下面選項的 custom
    if (EFData?.computationMethodOrEnergySource4_4 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = true;
    }

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be emtpy！",
        "The Company field cannot be emtpy！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat4_4/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat4_4 list data");
      console.error("Error fetching cat4_4 list data:", error);
      throw error;
    }
  },

  //////// 4.5新增排放活動
  async cat4_5AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "4_5",
      name: EAData?.itemName || "",
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat4_5/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat4_5 list data");
      console.error("Error fetching cat4_5 list data:", error);
      throw error;
    }
  },

  //////// cat5 計算方法
  async cat5Calculation(cat) {
    try {
      const response = await axiosInstance.get(
        `/api/cat5/calculation_dropdown_list/${cat}`,
      );

      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching Calculation dropdown list");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 5.1 活動下拉式選單
  async cat5_1EADropdownAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat5_1/dropdown_list`);
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 5.1 GHG 活動下拉式選單
  async ghg5_1EADropdownAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat5_1/dropdown_list/ghg`);
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 5.1新增排放活動
  async cat5_1AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "5_1",
      name: EAData.itemName || "",
      energySupplier: EFData?.energySupplier || "",
      customEnergyName: EFData?.customEnergyName || "",
      calculationId: EFData?.calculationId || 0,
      ghgCategory: cat || null,
      wasteManagementVendorName: EFData?.wasteManagementVendorName || "",
      activityId: toSafeNumber(EAData?.activityId),
      emissionFactorTypeId: toSafeNumber(EFData?.emissionFactorTypeId),
      emissionFactorId: toSafeNumber(EFData?.emissionFactorId),
      ghgActivityNameId: toSafeNumber(EAData?.ghgActivityNameId),
      standardId: standardId || 1,
    };

    if (EFData?.energyType5_1 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 51000000001;
    }

    if (EFData?.energyType5_1 === "Others") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 51000000000;
    }

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    if (!requestBody.activityId && cat !== "3_10") {
      handleAPIError(
        "The Activity Type field cannot be empty！",
        "The Activity Type field cannot be empty！",
      );
    }

    if (!requestBody.ghgActivityNameId && cat === "3_10") {
      handleAPIError(
        "The Activity Type field cannot be empty！",
        "The Activity Type field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat5_1/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat5_1 list data");
      console.error("Error fetching cat5_1 list data:", error);
      throw error;
    }
  },

  //////// 5.2 活動下拉式選單
  async cat5_2EADropdownAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat5_2/dropdown_list`);
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 5.2新增排放活動
  async cat5_2AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "5_2",
      name: EAData.itemName || "",
      activityId: toSafeNumber(EAData?.activityId),
      emissionFactorTypeId: toSafeNumber(EFData?.emissionFactorTypeId),
      emissionFactorId: toSafeNumber(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    if (EFData?.emissionSource5_2 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 52000000000;
    }

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    if (!requestBody.activityId) {
      handleAPIError(
        "The Activity Type field cannot be empty！",
        "The Activity Type field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat5_2/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat5_2 list data");
      console.error("Error fetching cat5_2 list data:", error);
      throw error;
    }
  },

  //////// 5.3 活動下拉式選單
  async cat5_3EADropdownAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat5_3/dropdown_list`);
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 5.3新增排放活動
  async cat5_3AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "5_3",
      name: EAData.itemName || "",
      calculationId: toSafeNumber(EAData?.calculationId),
      emissionFactorTypeId: toSafeNumber(EFData?.emissionFactorTypeId),
      emissionFactorId: toSafeNumber(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Waste Management Vendor Name field cannot be empty！",
        "The Waste Management Vendor Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat5_3/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat5_3 list data");
      console.error("Error fetching cat5_3 list data:", error);
      throw error;
    }
  },

  //////// 5.4新增排放活動
  async cat5_4AddEmissionActivityAPI(year, formData, EFData, cat, standardId) {
    const requestBody = {
      companyId: formData?.companyId,
      year: year,
      category: "5_4",
      name: formData.itemName || "",
      standardId: standardId || 1,
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
    };

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }
    if (!requestBody.name) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat5_4/`, requestBody);

      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat5_4 list data");
      console.error("Error fetching cat5_4 list data:", error);
      throw error;
    }
  },

  //////// 3.14 GHG 活動下拉式選單
  async ghg3_14EADropdownAPI() {
    try {
      const response = await axiosInstance.get(`/api/cat6_1/dropdown_list/ghg`);
      return formatter(response.data.data);
    } catch (error) {
      // handleAPIError(error, "Error fetching data:");
      console.error("Error fetching data:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////// 6.1新增排放活動
  async cat6_1AddEmissionActivityAPI(year, EAData, EFData, cat, standardId) {
    const requestBody = {
      companyId: EAData?.companyId,
      year: year,
      category: "6_1",
      name: EAData.itemName || "",
      energySupplier: EFData?.energySupplier || "",
      customEnergyName: EFData?.customEnergyName || "",
      calculationId: EFData?.calculationId || 0,
      ghgCategory: cat || null,
      wasteManagementVendorName: EFData?.wasteManagementVendorName || "",
      ghgActivityNameId: Number(EAData?.ghgActivityNameId),
      emissionFactorTypeId: Number(EFData?.emissionFactorTypeId),
      emissionFactorId: Number(EFData?.emissionFactorId),
      standardId: standardId || 1,
    };

    if (EFData?.energyType5_1 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 51000000001;
    }

    if (EFData?.energyType5_1 === "Others") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 51000000000;
    }

    //////// 判斷必填
    if (!requestBody.companyId) {
      handleAPIError(
        "The Company field cannot be empty！",
        "The Company field cannot be empty！",
      );
    }

    if (!requestBody.name) {
      handleAPIError(
        "The Activity Name field cannot be empty！",
        "The Activity Name field cannot be empty！",
      );
    }

    // ghgActivityNameId 只在 cat === "3_14" 時為必填
    if (!requestBody.ghgActivityNameId && cat === "3_14") {
      handleAPIError(
        "The Activity Type field cannot be empty！",
        "The Activity Type field cannot be empty！",
      );
    }

    try {
      const response = await axiosInstance.post(`/api/cat6_1/`, requestBody);
      ////////如果在查證中無法新增
      const { success, error: errMsg } = response.data;
      if (!success) {
        throw new Error(errMsg);
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error fetching cat6_1 list data");
      console.error("Error fetching cat6_1 list data:", error);
      throw error;
    }
  },

  ////////拿到某一筆排放活動
  // nxmap-prod-ver/NXMap_UI/src/EmissionView/API/emissionActivityAPI.js
  async editEmissionActivityAPI(cat, id, categoryISO64) {
    const url = `/api/${cat}/${id}`;
    const hasISO = Boolean(categoryISO64);
    const payload = hasISO ? { categoryISO64 } : undefined;
    const { data } = await axiosInstance[hasISO ? "post" : "get"](url, payload);
    return data?.data;
  },

  ////////修改某一筆排放活動
  async modifyEmissionActivityAPI(
    cat,
    formData,
    landEAFormData,
    EFFormData,
    category,
    EFLandFormData,
  ) {
    const hasValue = (v) => v !== undefined && v !== null;

    const safeAssign = (target, key, value, transform = (v) => v) => {
      if (hasValue(value)) target[key] = transform(value);
    };

    const requestBody = {};

    safeAssign(requestBody, "name", formData.itemName ?? formData.name);
    safeAssign(requestBody, "activityId", formData.activityId, Number);
    safeAssign(requestBody, "isFossil", formData.isFossil);
    safeAssign(
      requestBody,
      "equipmentCategoryId",
      formData.equipmentCategoryId,
      Number,
    );
    safeAssign(requestBody, "modeId", formData.modeId, Number);
    safeAssign(requestBody, "refrigerantId", formData.refrigerantId);
    safeAssign(
      requestBody,
      "refrigerantCalculationId",
      formData.refrigerantCalculationId,
      Number,
    );
    safeAssign(requestBody, "workingDays", formData.workingDays, Number);
    safeAssign(requestBody, "value", formData.value, Number);
    safeAssign(
      requestBody,
      "customFugitiveEmissionRate",
      formData.customFugitiveEmissionRate,
      Number,
    );
    safeAssign(
      requestBody,
      "transportActivityId",
      formData.transportActivityId,
      Number,
    );
    safeAssign(requestBody, "otherEFNote", EFFormData.otherEFNote);
    safeAssign(requestBody, "origin", formData.origin);
    safeAssign(requestBody, "originPort", formData.originPort);
    safeAssign(requestBody, "originAir", formData.originAir);
    safeAssign(requestBody, "destination", formData.destination);
    safeAssign(requestBody, "destinationPort", formData.destinationPort);
    safeAssign(requestBody, "destinationAir", formData.destinationAir);
    safeAssign(requestBody, "distance", formData.distance, Number);
    safeAssign(requestBody, "distancePort", formData.distancePort, Number);
    safeAssign(requestBody, "distanceAir", formData.distanceAir, Number);
    safeAssign(requestBody, "activityLandId", formData.activityLandId, Number);
    safeAssign(requestBody, "activityAirId", formData.activityAirId, Number);
    safeAssign(requestBody, "activitySeaId", formData.activitySeaId, Number);
    safeAssign(requestBody, "isMassTransit", formData.isMassTransit, Number);
    safeAssign(requestBody, "calculationId", formData.calculationId, Number);
    safeAssign(requestBody, "assetName", formData.assetName);
    safeAssign(requestBody, "vehicleModel", formData.vehicleModel);
    safeAssign(requestBody, "isStatic", formData.isStatic);
    safeAssign(requestBody, "isBiomassActivity", formData.isBiomassActivity);
    safeAssign(requestBody, "isBiomassSeparate", formData.isBiomassSeparate);
    //////// 4.1 4.2
    safeAssign(requestBody, "goodsName", formData.goodsName);
    //////// 5.1 5.2
    safeAssign(requestBody, "calculationId", EFFormData?.calculationId);
    safeAssign(requestBody, "energySupplier", EFFormData?.energySupplier);
    safeAssign(requestBody, "customEnergyName", EFFormData?.customEnergyName);
    safeAssign(
      requestBody,
      "wasteManagementVendorName",
      EFFormData?.wasteManagementVendorName,
    );
    //////// 3.10 GHG (5.1 64)
    safeAssign(requestBody, "ghgActivityNameId", formData.ghgActivityNameId);
    const specialCats = ["4_3"];

    if (formData.refrigerantCalculationId === 3) {
      if (formData.value <= 0 || formData.value > 100) {
        handleAPIError(
          "The Input value must be greater than 0 and less than or equal to 100.！",
          "The Input value must be greater than 0 and less than or equal to 100.！",
        );
      }
    }

    if (!specialCats.includes(category)) {
      requestBody.emissionFactorTypeId = EFFormData?.emissionFactorTypeId;
      requestBody.emissionFactorId = EFFormData?.emissionFactorId;
    } else {
      if (category === "4_3") {
        safeAssign(requestBody, "origin", landEAFormData?.origin);
        safeAssign(requestBody, "destination", landEAFormData?.destination);
        safeAssign(requestBody, "distance", landEAFormData?.distance, Number);
        safeAssign(
          requestBody,
          "transportActivityId",
          landEAFormData?.transportActivityId,
          Number,
        );
        safeAssign(requestBody, "isTransport", landEAFormData?.isTransport);
        safeAssign(
          requestBody,
          "emissionFactorTypeId",
          EFFormData?.emissionFactorTypeId,
          Number,
        );
        safeAssign(
          requestBody,
          "emissionFactorId",
          EFFormData?.emissionFactorId,
          Number,
        );
        safeAssign(
          requestBody,
          "landEFTypeId",
          EFLandFormData?.landEFTypeId,
          Number,
        );
        safeAssign(requestBody, "landEFId", EFLandFormData?.landEFId, Number);
      } else {
        requestBody.landEFType = EFLandFormData?.emissionTypeId;
        requestBody.landEFId = EFLandFormData?.emissionFactorId;
      }
    }

    //////// system database 底下的 custom
    if (EFFormData?.computationMethodOrEnergySource4_4 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = true;
    }

    //////// 5.1 5.2

    if (EFFormData?.energyType5_1 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 51000000001;
    }

    if (EFFormData?.energyType5_1 === "Others") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 51000000000;
    }

    if (EFFormData?.emissionSource5_2 === "Renewable Energy") {
      requestBody.emissionFactorTypeId = 2;
      requestBody.useCustomEFLevel = 52000000000;
    }

    try {
      const response = await axiosInstance.put(
        `/api/${cat}/${formData.id}`,
        requestBody,
      );
      if (response.data.success === false) {
        handleAPIError(null, {
          apiData: response.data
        });
      }

      return response.data;
    } catch (error) {
      // handleAPIError(error, "Error modifing data:");
      console.error("Error modifing data:", error);
      return null; // 失敗時回傳 null
      throw error;
    }
  },

  ////////刪除某一筆排放活動
  async deleteEmissionActivityAPI(cat, id) {
    const response = await axiosInstance.delete(`/api/${cat}/${id}`);
    return response.data;
  },

  ////////下載排放活動Excel
  async downloadEmissionActivityExcel(requestBody, routeCat) {
    try {
      const response = await axiosInstance.post(
        `/api/${routeCat}/download_activity_list_excel/`,
        requestBody,
        {
          responseType: "blob", // 設定回應類型為 Blob，確保能接收二進制檔案
          timeout: 300000,
        },
      );
      await throwIfBlobContainsAPIData(response);
      // 創建 Blob 物件
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // 創建下載連結
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      const matchFileName = contentDisposition.match(/filename="?([^"]+)"?/);
      link.href = downloadUrl;
      link.setAttribute("download", `${matchFileName[1]}`); // 設定下載的檔名
      document.body.appendChild(link);
      link.click(); // 自動點擊下載
      document.body.removeChild(link); // 下載後移除連結
      window.URL.revokeObjectURL(downloadUrl);
      return response;
    } catch (err) {
      console.error("Error downloading Excel", err);
      await normalizeBlobAPIError(err);
      throw err;
    }
  },

  ////////上傳Excel
  async uploadEmissionActivityEFExcel(formData, routeCat) {
    try {
      const response = await axiosInstance.post(
        `/api/${routeCat}/upload_activity_list_excel/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // 確保 Content-Type 正確
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  ////////取得已設定年份下拉式清單
  async getReportYearDropdownAPI(userId) {
    try {
      // const response = await axiosInstance.get(
      //   `/api/report/get_report_year_dropdown?userId=${userId}&reportId=${1}`
      // );
      // return response.data.data;

      const response = await axiosWithAbort("getReportYearDropdown", {
        method: "get",
        url: `/api/report/get_report_year_dropdown?userId=${userId}&reportId=${1}`,
      });

      return response.data.data;
    } catch (error) {
      // handleAPIError(error);
      console.error(error);
      return null;
    }
  },

  //////// 取得類別下的其他排放係數需要填寫備註的對照表
  async getEFOthersNoteMap(category) {
    try {
      const res = await axiosInstance.get(
        `/api/ef/get_other_ef_note_map/${category}`,
      );
      return res.data.data;
    } catch (err) {
      console.error(err);
    }
  },
};
